<script setup>
import { computed, useTemplateRef, ref, onMounted } from 'vue';
import { Letter } from 'vue-letter';
import { sanitizeTextForRender } from '@chatwoot/utils';
import { allowedCssProperties } from 'lettersanitizer';

import Icon from 'next/icon/Icon.vue';
import { EmailQuoteExtractor } from 'dashboard/helper/emailQuoteExtractor.js';
import FormattedContent from 'next/message/bubbles/Text/FormattedContent.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import EmailMeta from './EmailMeta.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';

import { useMessageContext } from '../../provider.js';
import { MESSAGE_TYPES } from 'next/message/constants.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import {
  buildResourceRewriter,
  hasRemoteResources,
} from 'dashboard/helper/emailRemoteResources';

const { content, contentAttributes, attachments, messageType } =
  useMessageContext();

const isExpandable = ref(false);
const isExpanded = ref(false);
const showQuotedMessage = ref(false);
const renderOriginal = ref(false);
const contentContainer = useTemplateRef('contentContainer');

onMounted(() => {
  isExpandable.value = contentContainer.value?.scrollHeight > 400;
});

const isOutgoing = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const originalEmailText = computed(() => {
  const text =
    contentAttributes?.value?.email?.textContent?.full ?? content.value;
  return sanitizeTextForRender(text);
});

const originalEmailHtml = computed(
  () =>
    contentAttributes?.value?.email?.htmlContent?.full ||
    originalEmailText.value
);

// HTML emails are designed for a light background; render them on a light
// "paper" in every theme so their own backgrounds keep readable text.
const hasHtmlContent = computed(() =>
  Boolean(contentAttributes?.value?.email?.htmlContent?.full)
);

// HTML emails bring their own colors and sizes. They stay out of prose-bubble,
// whose bubble colors are forced with !important and would override the email.
const letterClass = computed(() =>
  hasHtmlContent.value
    ? 'email-html !max-w-none letter-render'
    : 'prose prose-bubble !max-w-none letter-render'
);

const hasEmailContent = computed(() => {
  return (
    contentAttributes?.value?.email?.textContent?.full ||
    contentAttributes?.value?.email?.htmlContent?.full
  );
});

const messageContent = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original content
  return content.value;
});

const textToShow = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original text
  return originalEmailText.value;
});

const fullHTML = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original HTML
  return originalEmailHtml.value;
});

const unquotedHTML = computed(() =>
  EmailQuoteExtractor.extractQuotes(fullHTML.value)
);

const hasQuotedMessage = computed(() =>
  EmailQuoteExtractor.hasQuotes(fullHTML.value)
);

// Ensure unique keys for <Letter> when toggling between original and translated views.
// This forces Vue to re-render the component and update content correctly.
const translationKeySuffix = computed(() => {
  if (renderOriginal.value) return 'original';
  if (hasTranslations.value) return 'translated';
  return 'original';
});

// Imagens externas só carregam quando a pessoa pede, e só neste e-mail.
const showRemoteImages = ref(false);
const resourceRewriter = computed(() =>
  buildResourceRewriter({ allowRemote: showRemoteImages.value })
);
const hasBlockedRemoteImages = computed(
  () => !showRemoteImages.value && hasRemoteResources(fullHTML.value)
);

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <!-- Recebidos mostram a data no cabeçalho; enviados mantêm o rodapé com o status. -->
  <BaseBubble
    neutral-surface
    class="w-full overflow-hidden !rounded-xl"
    data-bubble-name="email"
    :hide-meta="!isOutgoing"
  >
    <EmailMeta class="px-4 pb-3 pt-4" />
    <div
      v-if="hasBlockedRemoteImages"
      class="mx-4 mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11"
      data-test-id="email-remote-images-blocked"
    >
      <span class="flex min-w-0 items-center gap-1.5">
        <Icon icon="i-hugeicons-image-not-found-01" class="size-4 shrink-0" />
        {{ $t('EMAIL_HEADER.REMOTE_IMAGES_BLOCKED') }}
      </span>
      <button
        type="button"
        class="font-semibold text-n-blue-11 hover:underline"
        data-test-id="email-show-remote-images"
        @click="showRemoteImages = true"
      >
        {{ $t('EMAIL_HEADER.SHOW_REMOTE_IMAGES') }}
      </button>
    </div>
    <section ref="contentContainer" class="px-4 pb-4">
      <div
        :class="{
          'max-h-[400px] overflow-hidden relative': !isExpanded && isExpandable,
          'overflow-y-scroll relative': isExpanded,
        }"
      >
        <div
          v-if="isExpandable && !isExpanded"
          class="absolute left-0 right-0 bottom-0 z-10 h-24 px-4 flex items-end bg-gradient-to-t from-n-solid-2 via-n-solid-2 to-transparent"
        >
          <button
            class="text-n-slate-12 py-2 px-8 mx-auto text-center flex items-center gap-2"
            @click="isExpanded = true"
          >
            <Icon icon="i-lucide-maximize-2" />
            {{ $t('EMAIL_HEADER.EXPAND') }}
          </button>
        </div>
        <FormattedContent
          v-if="isOutgoing && content && !hasEmailContent"
          class="text-n-slate-12"
          :content="messageContent"
        />
        <div v-else :class="{ 'email-paper': hasHtmlContent }">
          <Letter
            v-if="showQuotedMessage"
            :key="`letter-quoted-${translationKeySuffix}-${showRemoteImages}`"
            :class-name="letterClass"
            :allowed-css-properties="[
              ...allowedCssProperties,
              'transform',
              'transform-origin',
            ]"
            :html="fullHTML"
            :text="textToShow"
            :rewrite-external-resources="resourceRewriter"
          />
          <Letter
            v-else
            :key="`letter-unquoted-${translationKeySuffix}-${showRemoteImages}`"
            :class-name="letterClass"
            :html="unquotedHTML"
            :rewrite-external-resources="resourceRewriter"
            :allowed-css-properties="[
              ...allowedCssProperties,
              'transform',
              'transform-origin',
            ]"
            :text="textToShow"
          />
        </div>
        <button
          v-if="hasQuotedMessage"
          class="text-n-slate-11 px-1 leading-none text-sm bg-n-alpha-black2 text-center flex items-center gap-1 mt-2"
          @click="showQuotedMessage = !showQuotedMessage"
        >
          <template v-if="showQuotedMessage">
            {{ $t('CHAT_LIST.HIDE_QUOTED_TEXT') }}
          </template>
          <template v-else>
            {{ $t('CHAT_LIST.SHOW_QUOTED_TEXT') }}
          </template>
          <Icon
            :icon="
              showQuotedMessage
                ? 'i-lucide-chevron-up'
                : 'i-lucide-chevron-down'
            "
          />
        </button>
      </div>
    </section>
    <TranslationToggle
      v-if="hasTranslations"
      class="px-4 pb-3"
      :showing-original="renderOriginal"
      @toggle="handleSeeOriginal"
    />
    <section
      v-if="Array.isArray(attachments) && attachments.length"
      class="mx-4 mb-4 space-y-2 border-t border-n-weak pt-3"
    >
      <AttachmentChips :attachments="attachments" class="gap-1" />
    </section>
  </BaseBubble>
</template>

<style lang="scss">
// Light "paper" for HTML emails: redefine the tokens used by prose-bubble with
// the light palette so text stays dark on the email's own white background.
.email-paper {
  --slate-4: 232 232 236;
  --slate-6: 217 217 224;
  --slate-10: 128 131 141;
  --slate-11: 96 100 108;
  --slate-12: 28 32 36;
  --blue-11: 8 109 224;
  @apply rounded-lg border border-solid border-n-weak p-4;
  background-color: #fff;
  color: rgb(28 32 36);
  color-scheme: light;
  // The email's own z-index values stay inside this layer, below the
  // "Expandir e-mail" fade and the other bubble controls.
  position: relative;
  z-index: 0;
  isolation: isolate;
}

// Minimal base for HTML emails: :where() keeps zero specificity, so every color,
// size and spacing the email declares wins.
.email-html {
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;

  :where(h1) {
    font-size: 1.5rem;
    font-weight: 700;
  }

  :where(h2) {
    font-size: 1.25rem;
    font-weight: 700;
  }

  :where(h3) {
    font-size: 1.1rem;
    font-weight: 700;
  }

  :where(p) {
    margin: 0 0 0.75em;
  }

  :where(a) {
    color: rgb(var(--blue-11));
    text-decoration: underline;
  }

  :where(img) {
    max-width: 100%;
    height: auto;
  }
}

// Tailwind resets break the rendering of google drive link in Gmail messages
// This fixes it using https://developer.mozilla.org/en-US/docs/Web/CSS/Attribute_selectors

.letter-render [class*='gmail_drive_chip'] {
  box-sizing: initial;
  @apply bg-n-slate-4 border-n-slate-6 rounded-md !important;

  a {
    @apply text-n-slate-12 !important;

    img {
      display: inline-block;
    }
  }
}

// Email clients (Gmail, Outlook) hardcode dir="ltr" on wrapper elements.
// In RTL apps this forces email content LTR regardless of actual text.
[dir='rtl'] .letter-render [dir='ltr'] {
  direction: inherit;
}
</style>
